import { useState, useEffect, useCallback } from 'react';
import { CreditoService } from '@/services/creditoService';
import { useAuth } from '@/contexts/AuthContext';

interface UseBalanceReturn {
  saldo: number;
  loading: boolean;
  error: string | null;
  refreshBalance: () => Promise<void>;
  lastUpdated: Date | null;
}

export const useBalance = (userId?: string): UseBalanceReturn => {
  const { user, updateUser } = useAuth();
  const [saldo, setSaldo] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Usar userId passado como parâmetro ou pegar do contexto
  const targetUserId = userId || user?.id;

  const refreshBalance = useCallback(async (silent: boolean = false) => {
    if (!targetUserId) {
      
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    setError(null);
    
    try {
      
      
      const novoSaldo = await CreditoService.getSaldoUsuario(targetUserId);
      
      
      
      setSaldo(novoSaldo);
      setLastUpdated(new Date());
      
      // Atualizar contexto APENAS se o saldo realmente mudou
      if (user && user.id === targetUserId && user.saldo !== novoSaldo) {
        updateUser({ saldo: novoSaldo });
      }
      
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar saldo';
      
      setError(errorMessage);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [targetUserId, user, updateUser]);

  // Sincronizar com o saldo do contexto quando disponível
  useEffect(() => {
    if (user && user.id === targetUserId && user.saldo !== undefined) {
      
      setSaldo(user.saldo);
      setLastUpdated(new Date());
    }
  }, [user, targetUserId]);

  // Buscar saldo inicial
  useEffect(() => {
    if (targetUserId && (!user || user.saldo === undefined)) {
      
      refreshBalance();
    }
  }, [targetUserId, refreshBalance, user]);

  // 🔧 FIX: Refresh automático otimizado - 60s e apenas quando página visível
  useEffect(() => {
    if (!targetUserId) return;

    const interval = setInterval(() => {
      // Verificações adicionais para evitar refresh desnecessário
      if (!loading && document.visibilityState === 'visible') {
        
        refreshBalance(true); // silent refresh, sem spinner
      }
    }, 60000); // 🔧 Aumentado para 60 segundos

    return () => clearInterval(interval);
  }, [targetUserId, loading, refreshBalance]);

  return {
    saldo,
    loading,
    error,
    refreshBalance,
    lastUpdated
  };
};

// Hook específico para o usuário logado
export const useUserBalance = (): UseBalanceReturn => {
  const { user } = useAuth();
  return useBalance(user?.id);
};

export default useBalance; 